<script>
    import router from "page";
    import { onMount } from "svelte";
    import { isLoading, locale } from "../stores.js";

    import Modal from "../components/Modal.svelte";
    import DeleteModal from "../components/DeleteModal.svelte";

    import Tabulator from "tabulator-tables";
    import "tabulator-tables/dist/css/tabulator.min.css";

    import {
        fetchGet,
        fetchPost,
        fetchPatch,
        fetchDelete,
    } from "../utils/utilsFetch.js";

    import {
        productPictureLinkFormatter,
        numberFormatter,
        oneDecimalFormatter,
        percentFormatter,
        keywordFilter,
    } from "../utils/utilsTabulator.js";

    let table, data, canvasElement, tableElement;

    let showUserModal,
        user = {},
        changeUserPasswordInput;

    onMount(async () => {
        $isLoading = true;

        $locale = await fetchGet(`/api/locales/mk`);

        table = new Tabulator(tableElement, {
            height: "100%",
            data: [],
            layout: "fitDataFill",
            cellVertAlign: "middle",
            columnCalcs: "both",
            rowClick: function (e, row) {
                console.log("click");
                let user = row._row.data;
                getUser(user.id);
            },
            columns: [
                {
                    title: $locale?.companyKey || "",
                    field: "companyKey",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.client || "",
                    field: "companyName",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.email || "",
                    field: "email",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.cart || "",
                    columns: [
                        {
                            title: $locale?.products || "",
                            field: "cartSize",
                        },
                        {
                            title: $locale?.value || "",
                            field: "cartValue",
                        },
                    ],
                },
                {
                    title: $locale?.revenue || "",
                    field: "totalOrderValue",
                },
                {
                    title: $locale?.last || "",
                    columns: [
                        {
                            title: $locale?.lastLogin || "",
                            field: "lastLoginAt",
                        },
                        {
                            title: $locale?.order || "",
                            field: "lastOrderAt",
                        },
                    ],
                },
                {
                    title: $locale?.hiddenPrices || "",
                    field: "isHiddenPricesText",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.negativeStock || "",
                    field: "isNegativeStockAllowedText",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.onlyContractProducts || "",
                    field: "isOnlyContractProductsText",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.hiddenRebates || "",
                    field: "isRebateHiddenText",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
                {
                    title: $locale?.language || "",
                    field: "languageCode",
                    headerFilter: "input",
                    headerFilterFunc: keywordFilter,
                    headerFilterPlaceholder: "⌕",
                },
            ],
        });

        await getUsers();

        let chartData = await fetchGet(`/api/orders-chart`);

        let periods = chartData.map((item) => item.ym);
        let revenues = chartData.map((item) => item.value);

        let context = canvasElement.getContext("2d");
        new Chart(context, {
            type: "bar",
            data: {
                labels: periods,
                datasets: [
                    {
                        data: revenues,
                        label: $locale?.revenue || "",
                        backgroundColor: "black",
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                title: {
                    display: true,
                    text: $locale?.revenueMonthly || "",
                },
                tooltips: {
                    callbacks: {
                        label: function (tooltipItems, data) {
                            return tooltipItems.value.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ","
                            );
                        },
                    },
                },
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                callback: function (value, index, values) {
                                    if (parseInt(value) >= 1000) {
                                        return value
                                            .toString()
                                            .replace(
                                                /\B(?=(\d{3})+(?!\d))/g,
                                                ","
                                            );
                                    } else {
                                        return value;
                                    }
                                },
                            },
                        },
                    ],
                },
            },
        });

        $isLoading = false;
    });

    $: if (!showUserModal) {
        getUsers();
    }

    async function getUsers() {
        let users = await fetchGet(`/api/users`);
        table.replaceData(users);
    }

    async function getUser(userId) {
        user = await fetchGet(`/api/users/${userId}`);
        console.log(user);
        showUserModal = true;
    }

    async function changePassword(userId) {
        await fetchPatch(`/api/users/${userId}/password`, {
            password: changeUserPasswordInput,
        });
        changeUserPasswordInput = "";
    }

    async function changeLanguage(userId) {
        await fetchPatch(`/api/users/${userId}/language/${user.languageCode}`);
    }

    async function toggleIsHiddenPrices(userId) {
        await fetchPatch(`/api/users/${userId}/hidden-prices`, {
            isHiddenPrices: user.isHiddenPrices,
        });
    }

    async function toggleIsNegativeStockAllowed(userId) {
        await fetchPatch(`/api/users/${userId}/negative-stock`, {
            isNegativeStockAllowed: user.isNegativeStockAllowed,
        });
    }

    async function toggleIsOnlyContractProducts(userId) {
        await fetchPatch(`/api/users/${userId}/contract-products`, {
            isOnlyContractProducts: user.isOnlyContractProducts,
        });
    }

    async function toggleIsRebateHidden(userId) {
        await fetchPatch(`/api/users/${userId}/hidden-rebate`, {
            isRebateHidden: user.isRebateHidden,
        });
    }

    let showDeleteModal;
    async function deleteUser(userId) {
        await fetchDelete(`/api/users/${userId}`);
        showUserModal = false;
        table.replaceData(await fetchGet(`/api/users`));
    }

    let showAddUserModal,
        newUserCompanyKey,
        newUserLanguageCode,
        newUserEmail,
        newUserPassword,
        newUserIsHiddenPrices,
        newUserIsNegativeStockAllowed,
        newUserIsOnlyContractProducts,
        newUserIsRebateHidden;

    async function addNewUser() {
        console.log(
            newUserCompanyKey,
            newUserLanguageCode,
            newUserEmail,
            newUserPassword,
            newUserIsHiddenPrices,
            newUserIsNegativeStockAllowed,
            newUserIsOnlyContractProducts,
            newUserIsRebateHidden,
            newUserLanguageCode
        );

        await fetchPost(`/api/users`, {
            email: newUserEmail,
            companyKey: newUserCompanyKey,
            password: newUserPassword,
            isHiddenPrices: newUserIsHiddenPrices,
            isNegativeStockAllowed: newUserIsNegativeStockAllowed,
            isOnlyContractProducts: newUserIsOnlyContractProducts,
            isRebateHidden: newUserIsRebateHidden,
            languageCode: newUserLanguageCode,
        });

        newUserCompanyKey = "";
        newUserLanguageCode = "mk";
        newUserEmail = "";
        newUserPassword = "";
        newUserIsHiddenPrices = "";
        newUserIsNegativeStockAllowed = "";
        newUserIsOnlyContractProducts = "";
        newUserIsRebateHidden = "";

        await getUsers();

        showAddUserModal = false;
    }
</script>

<Modal bind:showModal={showAddUserModal}>
    <div slot="title">{$locale?.addNewUser || ""}</div>
    <div slot="body">
        <div class="form">
            <input
                placeholder={$locale?.clientCode || ""}
                bind:value={newUserCompanyKey}
            />
            <input
                placeholder={$locale?.email || ""}
                bind:value={newUserEmail}
            />

            <select bind:value={newUserLanguageCode}>
                <option value="mk">mk</option>
                <option value="en">en</option>
            </select>

            <input
                type="password"
                placeholder={$locale?.password || ""}
                bind:value={newUserPassword}
            />

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={newUserIsHiddenPrices}
                    style="margin-bottom: 0px; margin-right: 10px;"
                />
                {$locale?.hiddenPrices || ""}
            </div>

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={newUserIsNegativeStockAllowed}
                    style="margin-bottom: 0px; margin-right: 10px;"
                />
                {$locale?.negativeStock || ""}
            </div>

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={newUserIsOnlyContractProducts}
                    style="margin-bottom: 0px; margin-right: 10px;"
                />
                {$locale?.onlyContractProducts || ""}
            </div>

            <!-- <div
                style="display: flex; align-items: center;"
            >
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={newUserIsRebateHidden}
                    style="margin-bottom: 0px; margin-right: 10px;"
                />
                Скриени рабати
            </div> -->

            <button class="btn btn-blue" on:click={addNewUser}>
                {$locale?.add || ""}
            </button>
        </div>
    </div>
</Modal>

<Modal bind:showModal={showUserModal}>
    <div slot="title">{$locale?.userPreview || ""}</div>
    <div slot="body">
        <div class="user">
            <div>
                {user.companyKey} - {user.companyName}
            </div>

            <div>
                {user.email}
            </div>

            <select
                bind:value={user.languageCode}
                on:change={() => {
                    changeLanguage(user.id);
                }}
            >
                <option value="mk">mk</option>
                <option value="en">en</option>
            </select>

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={user.isHiddenPrices}
                    style="margin-bottom: 0px; margin-right: 10px;"
                    on:change={() => {
                        toggleIsHiddenPrices(user.id);
                    }}
                />
                {$locale?.hiddenPrices || ""}
            </div>

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={user.isNegativeStockAllowed}
                    style="margin-bottom: 0px; margin-right: 10px;"
                    on:change={() => {
                        toggleIsNegativeStockAllowed(user.id);
                    }}
                />
                {$locale?.negativeStock || ""}
            </div>

            <div style="display: flex; align-items: center;">
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={user.isOnlyContractProducts}
                    style="margin-bottom: 0px; margin-right: 10px;"
                    on:change={() => {
                        toggleIsOnlyContractProducts(user.id);
                    }}
                />
                {$locale?.onlyContractProducts || ""}
            </div>

            <!-- <div
                style="display: flex; align-items: center;"
            >
                <input
                    type="checkbox"
                    id="checkbox"
                    bind:checked={user.isRebateHidden}
                    style="margin-bottom: 0px; margin-right: 10px;"
                    on:change={() => {
                        toggleIsRebateHidden(user.id);
                    }}
                />
                Скриени рабати
            </div> -->

            <div style="display: flex; align-items:center;">
                <input
                    type="password"
                    placeholder={$locale?.password || ""}
                    bind:value={changeUserPasswordInput}
                />
                <button
                    class="btn btn-blue"
                    on:click={() => {
                        changePassword(user.id);
                    }}
                    style="margin-left: 10px;"
                >
                    {$locale?.change || ""}
                </button>
            </div>

            <button
                class="btn btn-red"
                on:click={() => {
                    showDeleteModal = true;
                }}
            >
                {$locale?.delete || ""}
            </button>
        </div>
    </div>
</Modal>

<DeleteModal
    bind:showDeleteModal
    deleteMessage={$locale?.areYouSureDeleteUser || ""}
    deleteCallback={() => {
        deleteUser(user.id);
    }}
/>

<svelte:head>
    <script
        type="text/javascript"
        src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/2.9.3/Chart.min.js"
    >
    </script>

    <link
        rel="stylesheet"
        type="text/css"
        href="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/2.9.3/Chart.min.css"
    />
</svelte:head>

<div class="container">
    <div class="header">
        <button
            class="btn btn-blue"
            on:click={() => {
                showAddUserModal = true;
            }}
        >
            {$locale?.addUser || ""}
        </button>
    </div>

    <div class="content">
        <div class="table" bind:this={tableElement} />
        <canvas class="chart" bind:this={canvasElement} />
    </div>
</div>

<style>
    .container {
        display: grid;
        grid-template-columns: 1fr;
        /* calc sprecuva tabelata da ide nadvor od content*/
        /* grid-template-rows: 30px 45px calc(100vh - 95px);  */
        grid-template-rows: auto 1fr;
        height: 100%;
    }

    .header {
        grid-row: 1/2;

        display: flex;
        flex-wrap: wrap;
        align-items: center;
        /* background: #ccc; */
        padding: 10px;
    }

    .content {
        padding: 0px 10px 10px 10px;
        grid-row: 2/-1;

        overflow: auto;
    }

    .user div {
        margin-bottom: 10px;
    }

    .user {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    .form {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
</style>
